<template>
  <div>
    <ContentBlock :required="true">
      <div slot="instructions">
        <h5>{{ $t('In the Headset') }}</h5>
        <p>{{ $t('How your content is displayed to end-users inside the headset.') }}</p>
      </div>
      <div class="basics">
        <UIInput
          :textarea="true"
          rows="7"
          :title="$t('Short Description')"
          :instructions="$t('This description will be seen by players when they preview your content in the headset. Max. characters: 400')"
          maxlength="400"
          required
          class="description"
          v-model="contentOwnership.shortDescription"
        />
        <div
          class="w-100 br4 cover flex items-center justify-center"
          :style="{backgroundImage: `url(${launcherImg})` }"
        >
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            x="0px"
            y="0px"
            viewBox="0 0 823.99 534.5"
            style="enable-background:new 0 0 823.99 484.5;"
            xml:space="preserve"
            class="w-70"
          >
            <g id="Layer_1">
              <image
                style="overflow:visible;opacity:.9"
                width="1699"
                height="999"
                xlink:href="~@/assets/preview_desc.png"
                transform="matrix(0.485 0 0 0.485 0 0)"
              />
            </g>
            <g id="Layer_2">
              <!-- <rect x="109.8" y="262.4" style="fill:none;" width="606.85" height="77.1" /> -->
              <text
                transform="matrix(1 0 0 1 109.797 273.0444)"
                style="font-family:'Open Sans';font-weight:700;font-size:14px;fill:white"
                v-if="!descriptionChunks || !descriptionChunks.length"
              >{{ $t('Your short description here') }}</text>
              <text v-else transform="matrix(1 0 0 1 109.797 273.0444)">
                <tspan
                  x="0"
                  y="0"
                  style="font-family:'Open Sans';font-weight:700;font-size:14px;fill:white"
                >{{descriptionChunks[0]}}</tspan>
                <tspan
                  x="0"
                  y="16.8"
                  style="font-family:'Open Sans';font-weight:700;font-size:14px;fill:white"
                >{{descriptionChunks[1]}}</tspan>
                <tspan
                  x="0"
                  y="33.6"
                  style="font-family:'Open Sans';font-weight:700;font-size:14px;fill:white"
                >{{descriptionChunks[2]}}</tspan>
                <tspan
                  x="0"
                  y="50.4"
                  style="font-family:'Open Sans';font-weight:700;font-size:14px;fill:white"
                >{{descriptionChunks[3]}}</tspan>
              </text>
              <!-- <rect x="109.8" y="116.4" style="fill:none;" width="366.85" height="77.1" /> -->
              <text
                transform="matrix(1 0 0 1 109.797 143.0039)"
                style="font-family:'Open Sans';font-weight:700;font-size:35px;fill:white"
              >{{ contentOwnership.title.length >= 20 ? `${contentOwnership.title.slice(0, 20)}...` : contentOwnership.title }}</text>
              <image
                style="fill:#9B1010;"
                x="528.65"
                y="77.5"
                width="182"
                height="85"
                v-bind="{'xlink:href': getMedia('image') && getMedia('image').src }"
                preserveAspectRatio="xMidYMid slice"
              />
              <!-- <rect x="528.65" y="77.5" style="fill:#9B1010;" width="182" height="85" /> -->
            </g>
          </svg>
        </div>
        <div class="w-50">
          <UIFormItem
            :title="$t('Short Gameplay Video')"
            :instructions="$t('This video will be seen when users preview your content in the headset.')"
          ></UIFormItem>
          <FileInputWithThumbnail
            :ratio="280 * 100 / 600"
            id="gameplayVideoMp4Url"
            type="video"
            :media="getMedia('gameplayVideoMp4Url')"
            class="mt2"
            :specs="$t('500MB maximum.')"
            @input="onFileUploadInput"
            @clear="onFileUploadClear"
          />
        </div>
      </div>
    </ContentBlock>
  </div>
</template>

<script>
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIFormItem from '@/core/shared/components/ui/UIFormItem';
import { useContentOwnershipMedia } from './compositions';
import { computed } from '@vue/composition-api';
import FileInputWithThumbnail from '@/core/shared/components/FileInputWithThumbnail';

const launcherImg = require('@/assets/skyline-bg.jpg');
export default {
  name: 'ContentLauncherForm',
  components: {
    ContentBlock,
    UIInput,
    UIFormItem,
    FileInputWithThumbnail,
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
  },
  setup (props) {
    function chunkSubstr (str, size) {
      const numChunks = Math.ceil(str.length / size);
      const chunks = new Array(numChunks);

      for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
        chunks[i] = str.substr(o, size);
      }

      return chunks;
    }
    const descriptionChunks = computed(() => {
      if (!props.contentOwnership.shortDescription) return;
      return chunkSubstr(props.contentOwnership.shortDescription, 80);
    });
    return {
      descriptionChunks,
      ...useContentOwnershipMedia(props.contentOwnership),
      launcherImg,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.tooltip {
  & >>> .popup {
    width: 40rem;
    @media (--tablet) {
      width: 80rem;
    }
  }
  & >>> .arrow-down {
    display: none;
  }
}
.basics {
  & > * {
    margin-bottom: var(--spacingMd);

    &:last-child {
      margin-bottom: 0;
    }
  }

  & .required {
    color: var(--colorSBRedFlat);
  }

  & .creators {
    & .developers {
      margin-bottom: var(--spacingMd);
    }

    @media (--tablet) {
      display: flex;

      & > * {
        flex: 1 1 auto;

        &:first-child {
          margin-right: var(--spacingMd);
        }
      }

      & .developers {
        margin-bottom: 0;
      }
    }
  }

  & .label {
    margin-bottom: var(--spacingXS);
  }

  & .helper {
    padding: var(--spacingXS) 0;
  }

  & .headset-type {
    & .buttons {
      display: grid;
      grid-gap: var(--spacingMd);

      @media (--tablet) {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  }
  & .video {
    grid-column-start: 1;
  }

  & .experience-type {
    @media (--tablet) {
      width: 50%;
    }
  }

  & .media {
    @media (--tablet) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: var(--spacingMd);
    }
  }
}
</style>
